// Sansara API client — cookie sessions + CSRF tokens, same-origin by default.
//
// Preview note: when deployed behind the sandbox preview proxy, the literal
// placeholder below is rewritten to the proxied backend path at deploy time.
// In that environment third-party cookies are blocked, so the client also
// keeps the session token in memory and sends it as a Bearer header
// (the server accepts both; production uses HttpOnly cookies only).
import { statusToMessage } from './errorCopy';

const PREVIEW_PROXY_BASE = '__PORT_8790__';
const DEPLOYED_BASE = PREVIEW_PROXY_BASE.startsWith('__PORT') ? '' : PREVIEW_PROXY_BASE;
const SAME_ORIGIN = import.meta.env.VITE_USE_SAME_ORIGIN_API !== 'false';
export const API_BASE: string =
  DEPLOYED_BASE || (SAME_ORIGIN ? '' : (import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:8787'));

let sessionToken: string | null = null;
export const clearSessionToken = () => {
  sessionToken = null;
};

export class ApiError extends Error {
  status: number;
  payload: unknown;
  constructor(status: number, message: string, payload?: unknown) {
    super(message);
    this.status = status;
    this.payload = payload;
  }
}

type Json = Record<string, unknown>;

/**
 * Everything that happens when the server refuses, in ONE place.
 *
 * `apiDownload` (the F18 holdings CSV) is a second entry point into the same API
 * with the same session, so it has to react to the same refusals — an idle-timed-
 * out 401 or a lapsed-plan 402 met while downloading must sign the user out and
 * surface the renewal state exactly as it does on every other call. A download
 * path with its own quieter error branch would be a second opinion about what a
 * 401 means, and the one that stays silent is the one that leaves a tab looking
 * signed in.
 *
 * Always throws.
 */
function throwForFailure(response: Response, path: string, payload: unknown): never {
  // Mandatory-2FA gate: the server blocks every app/data route for an
  // authenticated account that has not enrolled TOTP. Broadcast it so the
  // app can route the user into the enrollment screen from anywhere.
  if (
    response.status === 403 &&
    payload &&
    typeof payload === 'object' &&
    (payload as { code?: unknown }).code === 'TWO_FACTOR_ENROLLMENT_REQUIRED'
  ) {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('sansara:two-factor-required'));
    }
  }
  // Conv-26: with the 15-min idle timeout, a silent 401 is now the COMMON
  // path for a returning tab — without a global handler the SPA rendered a
  // wall of empty states that still LOOKED signed-in (the review's HIGH).
  // Broadcast session loss so AuthContext can clear the session and route
  // to sign-in with an honest "signed out after inactivity" notice. Auth
  // endpoints are excluded: a 401 there is a normal wrong-password/expired
  // -challenge response, not a lost session.
  if (response.status === 401 && !path.startsWith('/api/auth/')) {
    if (typeof window !== 'undefined') {
      try { sessionStorage.setItem('sansara.sessionLost', '1'); } catch { /* storage unavailable */ }
      window.dispatchEvent(new CustomEvent('sansara:session-lost'));
    }
  }
  // Conv-37: the sibling of the 401 above. A lapsed subscription 402s on every
  // /api route outside the documented exempt set, so without a global handler a
  // returning user gets the same wall of empty states that still LOOKS
  // subscribed. Broadcast it so the shell can show the renewal state and
  // re-read the session — the SESSION is still valid here, only the plan ended,
  // so this must NOT clear the session the way the 401 path does.
  if (
    response.status === 402 &&
    payload &&
    typeof payload === 'object' &&
    (payload as { code?: unknown }).code === 'SUBSCRIPTION_EXPIRED'
  ) {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent('sansara:subscription-expired', {
          detail: (payload as { access?: unknown }).access ?? null,
        }),
      );
    }
  }
  // Prefer the server's human sentence; every intentional refusal carries one.
  // When the body has no usable `error` (proxy 5xx HTML, empty body, a JSON
  // error missing the field), fall back to a friendly status-mapped sentence
  // instead of the old bare `Request failed (500)` — the one place a raw status
  // code used to reach the user.
  const serverMsg =
    payload && typeof payload === 'object' && 'error' in payload
      ? String((payload as { error: unknown }).error).trim()
      : '';
  const message = serverMsg || statusToMessage(response.status);
  throw new ApiError(response.status, message, payload);
}

async function request<T>(method: 'GET' | 'POST', path: string, body?: Json): Promise<T> {
  const headers: Record<string, string> = {};
  if (body) headers['Content-Type'] = 'application/json';
  if (sessionToken) headers['Authorization'] = `Bearer ${sessionToken}`;
  const response = await fetch(`${API_BASE}${path}`, {
    method,
    credentials: 'include',
    headers: Object.keys(headers).length ? headers : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  let payload: unknown = null;
  try {
    payload = await response.json();
  } catch {
    /* non-JSON response */
  }
  if (!response.ok) throwForFailure(response, path, payload);
  // Capture the raw session token when the server exposes it (preview mode).
  const maybeToken =
    payload && typeof payload === 'object' && 'session' in payload
      ? (payload as { session?: { token?: unknown } }).session?.token
      : undefined;
  if (typeof maybeToken === 'string' && maybeToken.length > 0) sessionToken = maybeToken;
  return payload as T;
}

export const apiGet = <T>(path: string) => request<T>('GET', path);
export const apiPost = <T>(path: string, body: Json = {}) => request<T>('POST', path, body);

/**
 * Fetch a file the server sends as an attachment (today: the F18 holdings CSV)
 * and hand it to the browser as a download.
 *
 * It goes through `fetch` rather than a plain `<a href="/api/…" download>` for
 * one reason that matters and one that follows from it. The link works only when
 * the API is same-origin — which it is in production and is NOT in local dev,
 * where `API_BASE` points at :8787 and a cross-origin navigation carries neither
 * the session cookie nor the bearer token, so the download would silently 401
 * for exactly the person testing it. And going through fetch means a refusal
 * lands in `throwForFailure` with every other refusal, instead of the browser
 * quietly saving a JSON error body as `sansara-holdings-….csv`.
 *
 * The filename comes from the server's `Content-Disposition` when present and
 * falls back to the caller's suggestion, never to a name built from anything the
 * page received.
 */
export async function apiDownload(path: string, fallbackFilename: string, body?: Record<string, unknown>): Promise<void> {
  const headers: Record<string, string> = {};
  if (sessionToken) headers['Authorization'] = `Bearer ${sessionToken}`;
  // A body makes it a POST — the CA-seat exports are RECORDED reads, and every
  // write runs under the server's write lock, which only covers non-GET.
  if (body) headers['Content-Type'] = 'application/json';
  const response = await fetch(`${API_BASE}${path}`, {
    method: body ? 'POST' : 'GET',
    credentials: 'include',
    headers: Object.keys(headers).length ? headers : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!response.ok) {
    let payload: unknown = null;
    try { payload = await response.json(); } catch { /* non-JSON refusal */ }
    throwForFailure(response, path, payload);
  }
  const disposition = response.headers.get('Content-Disposition') ?? '';
  const match = /filename="([A-Za-z0-9._-]+)"/.exec(disposition);
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = match ? match[1] : fallbackFilename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoking synchronously can cancel the download in Safari; one tick is enough
  // for the click to have been dispatched.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

// F63 — offline cached viewing.
// Wraps a GET so the last successful response for a path is kept in
// localStorage and served when the network is unavailable. Read-only data
// only (never auth, money movement, or secrets — callers pass non-sensitive
// read paths). The cache is per-path and best-effort; any storage error is
// swallowed so it can never break a live request.
const OFFLINE_PREFIX = 'sansara:offline:';

export interface OfflineResult<T> {
  data: T;
  stale: boolean; // true when served from cache because the network failed
  cachedAt: string | null;
}

export async function apiGetCached<T>(path: string): Promise<OfflineResult<T>> {
  const key = OFFLINE_PREFIX + path;
  try {
    const data = await apiGet<T>(path);
    try {
      localStorage.setItem(key, JSON.stringify({ at: new Date().toISOString(), data }));
    } catch {
      /* storage full / unavailable — ignore */
    }
    return { data, stale: false, cachedAt: new Date().toISOString() };
  } catch (err) {
    // Only fall back to cache for network/transport failures, never for auth
    // (401/403) or other server-rejected responses — those must surface.
    if (err instanceof ApiError) throw err;
    try {
      const raw = localStorage.getItem(key);
      if (raw) {
        const parsed = JSON.parse(raw) as { at: string; data: T };
        return { data: parsed.data, stale: true, cachedAt: parsed.at };
      }
    } catch {
      /* ignore parse/storage errors */
    }
    throw err;
  }
}

export const clearOfflineCache = () => {
  try {
    Object.keys(localStorage)
      .filter((k) => k.startsWith(OFFLINE_PREFIX))
      .forEach((k) => localStorage.removeItem(k));
  } catch {
    /* ignore */
  }
};

export const streamUrl = (symbols: string[]) =>
  `${API_BASE}/api/market/stream?symbols=${encodeURIComponent(symbols.join(','))}`;
